import React, { useEffect, useState } from "react";
import { useClientsManagement } from "../../contexts/ClientsManagementContext";
import { useNavigate } from "react-router-dom";
import { enqueueSnackbar } from "notistack";
import Loader from "../loading/Loader";
import MainButton from "../button/MainButton";
import TextField from "../fields/TextField";
import { Add, Copy, UserSquare, Link2, Eye } from "iconsax-react";
import CustomPagination from "../table/CustomPagination";
import MobileClientCard from "./MobileClientCard";

const ClientsListContent: React.FC = () => {
  const { clients, loading, currentPage, totalPages, fetchClients, addClient, getClientLink } = useClientsManagement();
  const navigate = useNavigate();

  const [clientName, setClientName] = useState("");
  const [isAddingClient, setIsAddingClient] = useState(false);
  const hasPreviousPage = currentPage > 1;
  const hasNextPage = currentPage < totalPages;

  useEffect(() => {
    fetchClients(currentPage);
  }, [currentPage]);

  const handlePageChange = (newPage: number) => {
    fetchClients(newPage);
  };

  const handleAddClient = async () => {
    if (!clientName.trim()) {
      enqueueSnackbar("Please enter a client name", { variant: "error" });
      return;
    }

    setIsAddingClient(true);
    try {
      await addClient({ name: clientName });
      setClientName("");
      enqueueSnackbar("Client added successfully!", { variant: "success" });
    } catch (err) {
      enqueueSnackbar("Failed to add client", { variant: "error" });
    } finally {
      setIsAddingClient(false);
    }
  };

  const handleCopyLink = (ref_id: string) => {
    const link = getClientLink(ref_id);
    navigator.clipboard
      .writeText(link)
      .then(() => {
        enqueueSnackbar("Link copied to clipboard", { variant: "success" });
      })
      .catch(() => {
        enqueueSnackbar("Failed to copy link", { variant: "error" });
      });
  };

  const handleClientClick = (clientId: number) => {
    navigate(`/dashboard/clients/${clientId}`);
  };

  if (loading && clients.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-4">
        <Loader />
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full overflow-hidden border divide-y divide-gorgonzola-blue-50 rounded-xl border-gorgonzola-blue-50">
      <div className="w-full p-4">
        <h2 className="font-bold text-closed-shutter-300">Clients Management</h2>
      </div>

      <div className="p-4">
        <div className="flex flex-col overflow-hidden border divide-y rounded-lg divide-gorgonzola-blue-50 border-gorgonzola-blue-50">
          <div className="p-4 bg-gorgonzola-blue-50/30">
            <h3 className="text-sm font-bold text-closed-shutter-300">Add New Client</h3>
          </div>

          <div className="p-4">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
              <div className="flex-1">
                <TextField label="Client Name" placeholder="Enter client name" value={clientName} onChange={(e) => setClientName(e.target.value)} required />
              </div>
              <MainButton type="primary" onClick={handleAddClient} disabled={isAddingClient || !clientName.trim()} customClass="w-full lg:w-auto justify-center">
                <Add className="size-5" variant="Broken" />
                <span>{isAddingClient ? "Adding..." : "Add Client"}</span>
              </MainButton>
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* {error && <div className="p-4 mb-4 text-sm border rounded-lg bg-english-vermillion-50 border-english-vermillion-300 text-english-vermillion-300">{error}</div>} */}

        {clients.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <UserSquare className="mb-4 size-16 text-gorgonzola-blue-200" variant="Broken" />
            <h3 className="mb-2 text-sm font-bold text-closed-shutter-300">No Clients Yet</h3>
            <p className="text-xs text-closed-shutter-300/70">Add your first client to get started</p>
          </div>
        ) : (
          <>
            {/* Mobile View */}
            <div className="block overflow-hidden border divide-y rounded-lg md:hidden divide-gorgonzola-blue-50 border-gorgonzola-blue-50 odd:*:bg-gorgonzola-blue-50 even:*:bg-white">
              {clients.map((client) => (
                <MobileClientCard
                  key={client.id}
                  client={client}
                  onViewClick={() => handleClientClick(client.id)}
                  onCopyClick={() => handleCopyLink(client.ref_id)}
                  clientLink={getClientLink(client.ref_id)}
                />
              ))}
            </div>

            {/* Desktop View */}
            <div className="hidden overflow-hidden border divide-y rounded-lg md:block divide-gorgonzola-blue-50 border-gorgonzola-blue-50">
              <div className="grid grid-cols-12 gap-4 p-4 text-xs font-bold bg-gorgonzola-blue-50/50 text-closed-shutter-300">
                <div className="col-span-3 text-center">Name</div>
                <div className="col-span-3 text-center">Reference ID</div>
                <div className="col-span-4 text-center">Link</div>
                <div className="col-span-2 text-center">Actions</div>
              </div>

              {clients.map((client) => (
                <div key={client.id} onClick={() => handleClientClick(client.id)} className="grid grid-cols-12 gap-4 p-4 text-xs transition-colors cursor-pointer hover:bg-gorgonzola-blue-50/30">
                  <div className="flex items-center justify-center col-span-3 font-bold text-closed-shutter-300">{client.name}</div>
                  <div className="flex items-center justify-center col-span-3 text-closed-shutter-300/70">{client.ref_id}</div>
                  <div className="flex items-center col-span-4 gap-2 overflow-hidden">
                    <Link2 className="size-4 min-w-4 text-gorgonzola-blue-400" variant="Broken" />
                    <span className="overflow-hidden text-gorgonzola-blue-400 text-ellipsis whitespace-nowrap line-clamp-1 w-max">{getClientLink(client.ref_id)}</span>
                  </div>
                  <div className="flex flex-col items-center col-span-2 gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCopyLink(client.ref_id);
                      }}
                      className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border rounded-lg text-gorgonzola-blue-400 border-gorgonzola-blue-200 hover:bg-gorgonzola-blue-50"
                    >
                      <Copy className="size-4" variant="Broken" />
                      <span>Copy</span>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleClientClick(client.id);
                      }}
                      className="w-fit flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors border rounded-lg text-closed-shutter-300 border-gorgonzola-blue-200 hover:bg-gorgonzola-blue-50"
                    >
                      <Eye className="size-4" variant="Broken" />
                      <span>View</span>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {clients.length > 0 && (
          <div className="mt-4">
            <CustomPagination currentPage={currentPage} totalPages={totalPages} hasPreviousPage={hasPreviousPage} hasNextPage={hasNextPage} onPageChange={handlePageChange} />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientsListContent;
